import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Skeleton, Typography } from "@mui/material";
import dayjs from "utils/dayjs";
import { Stack } from "components/layout/reusableLayouts";
import { Avatar, Card } from "@outpave/ds";
import { getDomain, logoLink } from "components/common/RenderMerchantDisplay";
import { formatCashFull } from "utils/formatCash";
import { ByocTransaction, ConnectedAccount } from "hooks/useByocDashboard";
import RecentTransactionsList from "./RecentTransactionsList";

export interface ConnectedAccountsPanelProps {
  accounts: ConnectedAccount[];
  transactions: ByocTransaction[];
  currency: string;
  /** Days the per-account spend figure covers, for the tooltip wording. */
  windowDays: number;
  loading?: boolean;
  transactionsLoading?: boolean;
}

const INSET = "var(--space-5)";

const AccountRow: React.FC<{
  account: ConnectedAccount;
  currency: string;
  windowDays: number;
  onOpen: () => void;
}> = ({ account, currency, windowDays, onOpen }) => {
  const { t } = useTranslation();

  // Plaid writes the institution name to Account.name and its website to
  // Account.url, so the logo comes from the URL. `institutionName` exists on
  // the type but is never written for BYOC, so it is deliberately unused.
  const logoSrc = account.url
    ? logoLink(getDomain(account.url), "icon", "lettermark", "light", 40)
    : undefined;

  const meta = [
    t("{{n}} cards", { n: account.cardCount }),
    account.refreshedAt
      ? t("synced {{when}}", { when: dayjs(account.refreshedAt).fromNow() })
      : t("not yet synced"),
  ].join(" · ");

  return (
    <Box
      component="button"
      type="button"
      onClick={onOpen}
      aria-label={account.name}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "var(--space-4)",
        width: "100%",
        padding: `var(--space-4) ${INSET}`,
        font: "inherit",
        color: "inherit",
        textAlign: "left",
        background: "none",
        border: "none",
        cursor: "pointer",
        transition: "background-color 0.15s ease",
        "&:hover": { backgroundColor: "var(--color-bg-elevated)" },
        "&:focus-visible": {
          outline: "var(--border-width-2) solid var(--color-border-focus)",
          outlineOffset: "-2px",
        },
      }}
    >
      <Avatar size="md" src={logoSrc} name={account.name} alt={account.name} />

      <Stack gap="var(--space-1)" sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h6" noWrap>
          {account.name}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "var(--color-text-tertiary)" }}
          noWrap
        >
          {meta}
        </Typography>
      </Stack>

      {/* Spend across the window, not a balance. No account-level balance
          exists for a linked card account: AccountBalance only reconciles
          FINANCIAL_ACCOUNT, and the per-card Plaid figure is a snapshot taken
          at link time that never refreshes. */}
      <Typography
        variant="h6"
        title={t("Spend in the last {{n}} days", { n: windowDays })}
        sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
      >
        {formatCashFull(account.spend, currency)}
      </Typography>
    </Box>
  );
};

/**
 * The company's linked card accounts, each with its card count, how fresh its
 * data is, and what it has spent, followed by the most recent activity across
 * all of them.
 */
const ConnectedAccountsPanel: React.FC<ConnectedAccountsPanelProps> = ({
  accounts,
  transactions,
  currency,
  windowDays,
  loading = false,
  transactionsLoading = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    // Everything above the transaction list holds its size: in a fixed-height
    // row the flex column would otherwise squeeze it instead of letting the
    // scroll area absorb the overflow.
    <Card
      variant="outlined"
      title={t("Connected accounts")}
      sx={{ height: "100%" }}
    >
      {loading && accounts.length === 0 ? (
        <Box sx={{ padding: INSET }}>
          <Skeleton variant="text" height={40} />
        </Box>
      ) : accounts.length === 0 ? (
        <Stack gap="var(--space-4)" sx={{ padding: "var(--space-8)" }}>
          <Typography
            variant="body1"
            sx={{ color: "var(--color-text-tertiary)", textAlign: "center" }}
          >
            {t("Link a card to start importing transactions")}
          </Typography>
        </Stack>
      ) : (
        // The account list scrolls inside its own capped area rather than
        // growing. At lg the page pins this row to a fixed height, so a list
        // that grew freely would eat the column and leave nothing for the
        // transactions below it, which is why they used to be hidden past two
        // accounts. Half the panel is the cap, leaving room for a meaningful
        // number of transaction rows however many accounts a company links.
        //
        // Below lg the row is content-sized, so the percentage does not resolve
        // and the cap simply does not apply: the panels stack and each grows to
        // fit, which is what that breakpoint wants anyway. One or two accounts
        // stay content-sized at every width, cap or no cap.
        <Box sx={{ flexShrink: 0, maxHeight: "50%", overflowY: "auto" }}>
          {accounts.map((account) => (
            <AccountRow
              key={account.id}
              account={account}
              currency={currency}
              windowDays={windowDays}
              onOpen={() => navigate("/admin/accounts")}
            />
          ))}
        </Box>
      )}

      <Box
        sx={{
          flexShrink: 0,
          padding: `var(--space-3) ${INSET}`,
          borderTop: "var(--border-width-1) solid var(--color-border-subtle)",
        }}
      >
        <Typography
          variant="label"
          sx={{ color: "var(--color-text-tertiary)" }}
        >
          {t("Recent transactions")}
        </Typography>
      </Box>

      {/* Scrolls rather than growing, so a long list cannot push this panel
          taller than the chart beside it. */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <RecentTransactionsList
          transactions={transactions}
          loading={transactionsLoading}
          inset={INSET}
        />
      </Box>
    </Card>
  );
};

export default ConnectedAccountsPanel;
